import { useEffect, useMemo, useRef, useState } from 'react';

import { parseSizeCssVar } from '@xemplo/common-util';
import { isValueItem } from '@xemplo/stateful-list-core';

import { useTriggerWidth } from '../../utils/hooks/use-trigger-width';
import { ListCssVar } from '../../utils/types/stateful-list.types';

import { ItemListWrapperProps } from './item-list-wrapper.types';

export function useItemListWrapper(props: ItemListWrapperProps) {
  const { renderItems, items, height = 300 } = props;
  const width = useTriggerWidth(props);

  const allValueItems = useMemo(
    () => items.groups.flatMap((g) => g.items).filter(isValueItem),
    [items]
  );

  const [scrollHeight, setScrollHeight] = useState(height);
  const nonScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // We have some requirements where the search and select all are fixed at the top of the list,
  // so we need to calculate the height of the scroll area based on the height of the
  // non-scrollable content (search, select all, etc.) and the total height of
  // the list. This allows us to have a fixed header and a scrollable area for the items.
  const baseHeight = useMemo(
    () => height - (nonScrollRef.current?.getBoundingClientRect().height ?? 0),
    [height]
  );

  // NOTE: Using the baseHeight causes the height coming from the ref to be 0 on the first render,
  // so we need to use the height prop instead. This is a workaround to ensure that
  // the scroll area height is set correctly on the first render.
  useEffect(() => {
    const scrollRef = nonScrollRef.current;
    if (!scrollRef) return;
    setScrollHeight(height - scrollRef.getBoundingClientRect().height);
  }, [height]);

  // The baseui scroll area does not work well with dynamic heights,
  // so we need to manually adjust the height of the scroll area content
  // based on the content height. This effect runs after the items have been rendered.
  useEffect(() => {
    if (!contentRef.current) return;

    const contentHeight = contentRef.current.getBoundingClientRect().height;
    setScrollHeight((prevHeight) => {
      // if the height is unchanged, return the previous height
      if (prevHeight === contentHeight) return prevHeight;

      // if the content height is less than the current scroll area height,
      // we can reduce the scroll area height to match the content height
      // This prevents unnecessary scrollbars when the content is smaller than the scroll area.
      return contentHeight < baseHeight ? contentHeight : baseHeight;
    });
    // The renderItems dependency is used to trigger this effect when the items change.
    // It usually changes based on the search input
  }, [renderItems, baseHeight]);

  return {
    items: allValueItems,
    style: {
      ...parseSizeCssVar({ cssVar: ListCssVar.WrapperWidth, value: width ?? 'auto' }),
      ...parseSizeCssVar({ cssVar: ListCssVar.WrapperHeight, value: height }),
      ...parseSizeCssVar({ cssVar: ListCssVar.ScrollAreaHeight, value: scrollHeight }),
    },
    nonScrollRef,
    contentRef,
    renderItems,
  };
}
